<template>
  <div id="aula-login">
    <div v-if="isPresenceLogin" class="main-toastr-container">
      <toastr ref="errorToastr" variant="warning" icon-font-size="1em" circle-background="white">
        {{ $getText(errorText) }}
      </toastr>
    </div>
    <div id="main">
      <b-container>
        <b-btn
          v-if="!(isLoginPage || $route.name.includes('institution'))"
          variant="link"
          class="aula-login-back"
          :aria-label="$getText('ARIA_LABEL_BACK')"
          @click="goBack"
        >
          <i class="icon-Aula_app_arrow_slideback" />
        </b-btn>
        <LoginInfo />
        <div class="aula-logo" role="heading" aria-level="1">
          <span class="sr-only">{{ $getText('ARIA_LABEL_AULA_LOGIN') }}</span>
          <i class="icon-Logo" aria-hidden="true" />
        </div>
        <template v-if="isInstitutionLoginPath">
          <institution-login />
        </template>
        <b-row>
          <b-col v-if="$route.name === 'login'" cols="12" class="login-method">
            {{ $getText('LOGIN_PAGE_CHOOSE_LOGIN_METHOD') }}
          </b-col>
          <b-col v-else-if="$route.name === 'presenceLogin'" cols="12" class="employee-login-method">
            {{ $getText('LOGIN_PAGE_CHOOSE_LOGIN_METHOD_EMPLOYEE') }}
          </b-col>
        </b-row>
        <b-row v-if="$route.name == 'otherOptionsLogin'" class="justify-content-md-center mt-2">
          <b-col cols="12" class="remember">
            <b-form-checkbox id="rememberSelection" v-model="rememberLoginMethod">
              {{ $getText('REMEMBER_PROFILE_ROLE') }}
            </b-form-checkbox>
          </b-col>
        </b-row>
        <template v-if="isLoginPage">
          <b-row class="justify-content-md-center mt-4 box-container">
            <b-col cols="12" lg="4">
              <div
                class="square-box uni-login"
                tabindex="0"
                role="button"
                @click="selectMethod('unilogin')"
                @keydown.enter="selectMethod('unilogin')"
              >
                <div class="content d-flex flex-column align-items-start">
                  <span class="login-method">{{ $getText('UNI_LOGIN') }}<br /></span>
                  <div class="mit-id-logo-container">
                    <img :src="imageUrl" class="mit-id-logo" :alt="$getText('UNI_LOGIN')" />
                  </div>
                  <span class="login-method">{{ $getText('LOKALT_LOGIN') }}<br /></span>
                </div>
                <i v-if="!isMobile" class="icon-Aula_forward-arrow" />
              </div>
            </b-col>
            <b-col cols="12" lg="4">
              <div
                class="square-box"
                tabindex="0"
                role="button"
                @click="selectMethod('other')"
                @keydown.enter="selectMethod('other')"
              >
                <div class="content">
                  {{ $getText('LOGIN_OTHER_OPTIONS') }}<br />
                  <span class="employee-only">{{ $getText('LOGIN_OPTIONS_FOR_EMPLOYEE_ONLY') }}</span>
                </div>
                <b-form-checkbox
                  v-if="showCookie"
                  id="remember"
                  v-model="rememberLoginMethod"
                  class="remember-checkbox"
                  @click.native.stop
                >
                  <span class="text">{{ $getText('USE_SAVED_CHOICE') }}</span>
                </b-form-checkbox>
                <i v-if="!isMobile" class="icon-Aula_forward-arrow" />
              </div>
            </b-col>
          </b-row>
        </template>
        <LoginAnnouncement v-if="$route.name === 'login'" />
        <template v-if="$route.name == 'otherOptionsLogin'">
          <b-row class="justify-content-md-center mt-4">
            <b-col cols="12" lg="4">
              <div
                class="square-box"
                tabindex="0"
                role="button"
                @click="selectOptionLogin('contextHandler')"
                @keydown.enter="selectOptionLogin('contextHandler')"
              >
                <div class="content">
                  {{ $getText('CONTEXT_HANDLER') }}
                </div>
                <i class="icon-Aula_forward-arrow" />
              </div>
            </b-col>
            <b-col cols="12" lg="4">
              <div
                class="square-box last"
                tabindex="0"
                role="button"
                @click="selectOptionLogin('municipalIdp')"
                @keydown.enter="selectOptionLogin('municipalIdp')"
              >
                <div class="content">
                  {{ $getText('MUNICIPALITY_IDP') }}
                </div>
                <i class="icon-Aula_forward-arrow" />
              </div>
            </b-col>
          </b-row>
        </template>
        <municipal-login v-if="$route.name == 'municipalIdP'" />
      </b-container>
      <div class="footer-links">
        <a role="button" :href="AULA_URLS.OPERATING_STATUS" target="_blank">
          <span>
            {{ $t('label.operating_status_info') }}
          </span>
          <Icon class="pl-2" :name="iconClassEnum.EXPORT" />
        </a>
        <a role="button" :href="AULA_URLS.ACCESSIBILITY_STATEMENT" target="_blank">
          <span>
            {{ $getText('ACCESSIBILITY_STATEMENT') }}
          </span>
          <Icon class="pl-2" :name="iconClassEnum.EXPORT" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { types } from '../../../src/store/types/types';
import { mapGetters } from 'vuex';
import KommunalIDPLogin from './KommunalIDPLogin.vue';
import { Cookie } from '../../assets/plugins/cookie';
import $ from 'jquery';
import InstitutionLogin from './InstitutionLogin';
import { providerKeyTypes } from '../../enums/providerKeyTypes';
import LoginAnnouncement from './LoginAnnouncement.vue';
import LoginInfo from './LoginInfo.vue';
import Icon from '../Icon.vue';
import { iconClassEnum } from '../../enums/iconClassEnum';
import { AULA_URLS } from '../../constants/urls';
import { useUserProfileStore } from '../../stores';

export default {
  inject: {
    getCanUseInstitutionLogin: {
      from: providerKeyTypes.canUseInstitutionLogin,
      default: () => () => false,
    },
  },
  data: function () {
    return {
      rememberLoginMethod: false,
      storedCookie: false,
      AULA_URLS,
    };
  },
  computed: {
    iconClassEnum() {
      return iconClassEnum;
    },
    ...mapGetters({
      isMobile: types.GET_IS_MOBILE,
      errorText: types.GET_ERROR_TEXT,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    isProfileLoaded() {
      return this.userProfileStore.isLoading === false;
    },
    imageUrl() {
      return window.location.origin + '/static/MitID_LOGO_CLEAN_WHITE.png';
    },
    isLoginPage() {
      return ['login', 'presenceLogin'].includes(this.$route.name);
    },
    isPresenceLogin() {
      return this.$route.name === 'presenceLogin';
    },
    showCookie() {
      return Cookie.Read('rememberLoginMethod') == 'true';
    },
    isInstitutionLoginPath() {
      return this.$route.name == 'institution';
    },
  },
  watch: {
    errorText() {
      if (this.errorText != '' && this.$refs.errorToastr != undefined) {
        this.$refs.errorToastr.show();
      }
    },
  },
  mounted() {
    this.rememberLoginMethod = Cookie.Read('rememberLoginMethod') === 'true';
    this.storedCookie = Cookie.Read('rememberLoginMethod') == 'true';
    if (this.$route.name !== 'presenceLogin') {
      let domain;
      if (window.location.hostname.indexOf('aula.dk') !== -1) {
        domain = '.' + window.location.hostname.split('.').slice(-2).join('.');
      }
      Cookie.Erase('EmployeeLoginToCheckinDashboard', domain);
      Cookie.Erase('PresenceConfigurationChecked');
    }
    if (this.shouldRedirect()) {
      this.$router.push({ name: 'overblik' });
    }
  },
  methods: {
    shouldRedirect() {
      let redirect = false;
      if (this.isProfileLoaded) {
        redirect = true;
      }

      if (this.isInstitutionLoginPath && this.getCanUseInstitutionLogin()) {
        redirect = false;
      }

      return redirect;
    },
    clearCookies() {
      Cookie.Erase('rememberLoginMethod');
      Cookie.Erase('loginMethod');
      if (Cookie.Read('IDP') !== null) {
        Cookie.Erase('IDP');
      }
    },
    selectMethod(method) {
      if (method == 'unilogin') {
        if (!this.rememberLoginMethod && this.storedCookie) {
          this.clearCookies();
        }
        window.location.href = '/auth/login.php?type=unilogin';
      } else {
        if (this.rememberLoginMethod && this.storedCookie) {
          if (Cookie.Read('loginMethod') == 'contextHandler') {
            window.location.href = '/auth/login.php?type=context_handler';
          } else {
            if (Cookie.Read('IDP') !== null) {
              const payload = JSON.parse(Cookie.Read('IDP'));
              const fullHost =
                window.location.protocol +
                '//' +
                window.location.hostname +
                (window.location.port ? ':' + window.location.port : '');
              window.location.href = fullHost + '/auth/login.php?' + $.param(payload);
            } else {
              this.$router.push({ name: 'municipalIdP' });
            }
          }
        } else {
          this.$router.push({ name: 'otherOptionsLogin' });
        }
      }
    },
    selectOptionLogin(method) {
      if (this.rememberLoginMethod) {
        Cookie.Create('loginMethod', method, 365);
        Cookie.Create('rememberLoginMethod', 'true', 365);
      } else {
        this.clearCookies();
      }
      if (method == 'contextHandler') {
        window.location.href = '/auth/login.php?type=context_handler';
      } else {
        this.$router.push({ name: 'municipalIdP' });
      }
    },
    goBack() {
      this.$router.go(-1);
    },
  },
  components: {
    Icon,
    LoginInfo,
    LoginAnnouncement,
    'municipal-login': KommunalIDPLogin,
    'institution-login': InstitutionLogin,
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '../../assets/scss/core/variables' as *;
@use '../../assets/scss/core/breakpoints' as *;
@use '../../assets/scss/components/login/login' as *;

#aula-login {
  .login-method {
    font-size: 24px;
    padding: 16px 0;
  }

  ::v-deep .login-info {
    position: fixed;
    right: 40px;
    bottom: 40px;
    z-index: 1;

    @include breakpoint-lg-down() {
      top: 16px;
      right: 16px;
      bottom: unset;
    }
  }

  .login-announcement {
    padding: 56px 40px;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  @include breakpoint-lg-down() {
    .login-method {
      font-size: 13px;
      padding: 0;
    }
    .login-announcement {
      position: static;
      padding: 0;
    }
  }
}

.main-toastr-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;

  @include breakpoint-sm-down() {
    width: auto;
    top: 20px;
    right: 20px;
    left: 20px;
  }
}
</style>
